import AlertPopup from "components/alert-popup/AlertPopup";
import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import ResidencePermitForm from "components/form/residence-permit/ResidencePermitForm";
import StaffCorporateInfoForm from "components/form/staff/StaffCorporateInfoForm";
import StaffPersonalInfoForm from "components/form/staff/StaffPersonalInfoForm";
import ValidationModal from "components/modal/ValidationModal";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import {
  QUERY_STATUS,
  POPUP_STATUS,
  VISA,
  FILTERS_FOR_NAMES,
  STAFF_PROFILES,
} from "constants/constants";
import { formatCreatedData } from "utils/staffHelper";
import { useBranchesContext } from "api/settings/branches/BranchesProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFileContext } from "api/files/FilesProvider";
import { useForm } from "react-hook-form";
import { useJobContext } from "api/settings/job/JobProvider";
import { useNavigate } from "react-router-dom";
import { useReasonLeaveContext } from "api/settings/reason-leave/ReasonLeaveProvider";
import { useRecruitmentMethodContext } from "api/settings/recruitment-method/RecruitmentMethodProviders";
import { useStaffContext } from "api/directory/staff/StaffProvider";
import { useSyntecContext } from "api/settings/syntec/SyntecProvider";
import { useUserContext } from "api/user/UserProvider";
import { useProfileUploadSetup, useWindowSize } from "utils/customHooks";
import {
  STAFF_FORM_DEFAULT_VALUES,
  STAFF_FORM_LABELS,
  STAFF_FORM_PLACEHOLDERS,
} from "constants/forms";
import { useProfileContext } from "api/settings/profile/ProfileProvider";
import { useNationalitiesContext } from "api/settings/nationalities/NationalitiesProvider";
import { useContractualStatusContext } from "api/settings/contractual-status/ContractualStatusProvider";
import { useRightsContext } from "api/rights/RightsProvider";
import { ROLE_DUTLER, ROLE_FMD, ROLE_JOB, ROLE_NDF } from "access-roles/roles";
import colors from "assets/styles/mixins/colors.scss";
import { ENTITY } from "constants/info-header";
import { handleSyntecCoefficientId } from "utils/helper";
import { useGeneralContext } from "api/directory/general/GeneralProvider";

const CreateStaff = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    reset,
    unregister,
    setError,
  } = useForm({
    defaultValues: STAFF_FORM_DEFAULT_VALUES,
  });
  const { profileList, profileListGetter, profileByIdGetter, profilePerId } =
    useProfileContext();
  const { fetchBranches, branches } = useBranchesContext();
  const { jobList, jobListGetter } = useJobContext();
  const { lang, userScope, setApiErrorResponse } = useUserContext();
  const { reasonLeaveList, reasonLeaveListGetter } = useReasonLeaveContext();
  const { recruitmentMethodList, recruitmentMethodListGetter } =
    useRecruitmentMethodContext();
  const { getAllRoles, roleList } = useRightsContext();
  const {
    createStaff,
    createStaffStatus,
    createdStaff,
    apiErrorResponse,
    getStaffBySlug,
  } = useStaffContext();
  const { syntecCoefStaffList, syntecCoefStaffListGetter } = useSyntecContext();
  const { nationalitiesList, nationalitiesListGetter } =
    useNationalitiesContext();
  const { createdFile, setCreatedFile, createFile, uploadProfilePicture } =
    useFileContext();
  const { contractualStatusList, contractualStatusListGetter } =
    useContractualStatusContext();
  const navigate = useNavigate();
  const size = useWindowSize();
  const { picture, tempUserId } = useProfileUploadSetup();
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const [isOpen, setIsOpen] = useState(false);
  const [showSection, setShowSection] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  const flattedsyntecCoefList = syntecCoefStaffList?.flatMap((e) =>
    e.coef_position_syntec.map((elm) => ({
      ...elm,
      value: `${
        elm.position + (elm.coefficient > 0 ? " " + elm.coefficient : "")
      }`,
    }))
  );

  useEffect(() => {
    nationalitiesListGetter();
  }, [lang]);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  useEffect(() => {
    recruitmentMethodListGetter();
    fetchBranches();
    jobListGetter();
    reasonLeaveListGetter();
    syntecCoefStaffListGetter();
    profileListGetter();
    getAllRoles([ROLE_DUTLER, ROLE_NDF, ROLE_FMD, ROLE_JOB]);
    contractualStatusListGetter();
  }, []);

  const onSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await createStaff(info);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const getOptions = (search) => {
    getNamesByFilters({
      profile: [...STAFF_PROFILES, "cslt", "str"],
      types: [FILTERS_FOR_NAMES.PERSON],
      search,
    });
  };

  const formatData = (data) => {
    onSubmit(
      formatCreatedData({
        ...data,
        nationality_id: data.nationality,
        syntec_coef_position_staff: handleSyntecCoefficientId({
          data,
          contractualStatusList,
          flattedsyntecCoefList,
          entity: ENTITY.STAFF,
        }),
      })
    );
    return setIsOpen(true);
  };

  const resetForm = () => {
    reset(STAFF_FORM_DEFAULT_VALUES);
    window.scrollTo(0, 0);
  };
  const [code, setCode] = useState();
  const [status, setStatus] = useState();

  const onError = () => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(FIELD_REQUIRED);
  };

  useEffect(() => {
    setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (apiErrorResponse) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
    }
  }, [apiErrorResponse]);

  const value = useMemo(
    () => ({
      apiErrorResponse,
      nationalities: nationalitiesList,
      cellHighlight: cellHighlight,
      isValidated: isValidated,
      branches: branches,
      davFunctions: jobList,
      getOptions,
      isMobile: size.width < 600,
      lang: lang,
      leavingReasons: reasonLeaveList,
      personsByProfileList: namesByFilters,
      recruitmentMethodList: recruitmentMethodList,
      profileList: profileList,
      profileByIdGetter: profileByIdGetter,
      profilePerId: profilePerId,
      roleList: roleList,
      syntecCoefStaffList: flattedsyntecCoefList,
      contractualStatusList,
      createFile,
      uploadProfilePicture,
      createdFile,
      setCreatedFile,
      picture,
      setStatus,
      setCode,
      tempUserId,
    }),
    [
      apiErrorResponse,
      nationalitiesList,
      cellHighlight,
      branches,
      jobList,
      lang,
      namesByFilters,
      reasonLeaveList,
      recruitmentMethodList,
      syntecCoefStaffList,
      profileList,
      profilePerId,
      contractualStatusList,
      createFile,
      uploadProfilePicture,
      createdFile,
      setCreatedFile,
      picture,
      roleList,
      tempUserId,
    ]
  );

  return (
    <>
      <ValidationModal
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          if (createStaffStatus === QUERY_STATUS.ERROR) return;
          else if (
            createStaffStatus === QUERY_STATUS.SUCCESS &&
            createdStaff?.slug
          ) {
            navigate(`/${userScope}/staff/${createdStaff?.slug}`);
            getStaffBySlug(createdStaff?.slug);
          }
        }}
        status={createStaffStatus}
      />
      <InfoContext.Provider value={value}>
        <>
          <AlertPopup
            code={code}
            setCode={setCode}
            setStatus={setStatus}
            status={status}
          />
          <form
            onSubmit={(e) => {
              clearErrors();
              handleSubmit(formatData, onError)(e);
            }}
            style={{ paddingTop: "2rem" }}
          >
            <StaffPersonalInfoForm
              setValue={(field, value) => {
                setValue(field, value);
                clearErrors(field);
              }}
              watch={watch}
              control={control}
              register={register}
              errors={errors}
              unregister={unregister}
            />
            <br />
            <ResidencePermitForm
              showSection={showSection}
              setShowSection={setShowSection}
              action={VISA.ADD}
              setValue={(field, value) => {
                setValue(field, value);
                clearErrors(field);
              }}
              watch={watch}
              control={control}
              register={register}
              labels={STAFF_FORM_LABELS}
              placeholders={STAFF_FORM_PLACEHOLDERS}
            />
            <StaffCorporateInfoForm
              setValue={(field, value) => {
                setValue(field, value);
                clearErrors(field);
              }}
              watch={watch}
              control={control}
              register={register}
              errors={errors}
              unregister={unregister}
              clearErrors={clearErrors}
            />
            <div className="form-buttons">
              <Button
                className="btn-standard btn-outline"
                onClick={resetForm}
                type="button"
                id="button-form-reset"
              >
                <FormattedMessage id="cancel" defaultMessage="Annuler" />
              </Button>
              <Button
                color="btn-medium gray-700"
                backgroundColor={
                  createStaffStatus === QUERY_STATUS.LOADING &&
                  colors.disabledGrey
                }
                disabled={createStaffStatus === QUERY_STATUS.LOADING}
                type="submit"
                id="button-form-validation"
                onClick={() => setIsValidated(true)}
              >
                <FormattedMessage
                  id="button_form_validation"
                  defaultMessage="Valider la création"
                />
              </Button>
            </div>
          </form>
        </>
      </InfoContext.Provider>
    </>
  );
};

export default CreateStaff;
